import React, { useCallback, useState } from "react";

import { Col } from "antd/es/grid";
import Row from "antd/es/row";
import Select from "antd/es/select";
import { useIntl } from "react-intl";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import User from "@mapmycustomers/shared/types/User";
import { SelectField } from "@mapmycustomers/ui";

import ActivityTypeSelector from "@app/component/input/ActivityTypeSelector";
import UserMultiSelectField from "@app/component/input/UserMultiSelectField";
import RecapRange from "@app/enum/preview/RecapRange";
import layout from "@app/styles/layout";
import { AnalyticsService, useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { getActivityStatusOptionDisplayName } from "@app/util/ui";

import styles from "./FilterBar.module.scss";
import useRecapRangeOptions from "./useRecapRangeOptions";

interface Props {
  activityTypes: ActivityType[];
  analyticIssuer?: AnalyticsService;
  filter: ActivityStatusOption;
  onChangeActivityTypes: (activityTypes: ActivityType[]) => void;
  onChangeAssignees: (assignees: User["id"][]) => void;
  onChangeRecapRange: (recapRange: RecapRange) => void;
  onFilterChange: (filter: ActivityStatusOption) => void;
  recapRange: RecapRange;
  selectedActivityTypes: ActivityType[];
  selectedAssignees: User["id"][];
  users: User[];
}

const FilterBar: React.FC<Props> = ({
  activityTypes,
  analyticIssuer,
  filter = ActivityStatusOption.ALL,
  onChangeActivityTypes,
  onChangeAssignees,
  onChangeRecapRange,
  onFilterChange,
  recapRange,
  selectedActivityTypes,
  selectedAssignees,
  users,
}) => {
  const intl = useIntl();
  const activitiesAnalytics = useExtendedAnalytics("Activities", analyticIssuer);
  const [appliedFilter, setAppliedFilter] = useState(filter);

  const handleActivityTypeChange = useCallback(
    (values: Array<ActivityType>) => {
      if (1 <= values.length) {
        onChangeActivityTypes(values);
      }
    },
    [onChangeActivityTypes]
  );

  const handleFilterChange = useCallback(
    (value) => {
      activitiesAnalytics?.clicked(["Sort Activities Dropdown"], { filter: value });
      setAppliedFilter(value);
      onFilterChange(value);
    },
    [activitiesAnalytics, onFilterChange]
  );

  const recapOptions = useRecapRangeOptions(intl, appliedFilter);

  return (
    <div className={styles.container}>
      <Row gutter={layout.spacerM}>
        <Col span={15}>
          <span>
            {intl.formatMessage({
              id: "preview.activityList.filter.assignee",
              defaultMessage: "Assignees",
              description: "Assignees filter on company record pane",
            })}
          </span>
          <UserMultiSelectField
            className={styles.container}
            onChange={onChangeAssignees}
            oneLiner
            placeholder={intl.formatMessage({
              id: "preview.activityList.filter.assignee.placeholder",
              defaultMessage: "All assignees",
              description: "Placeholder of the Assignee filter on activity list page",
            })}
            users={users}
            value={selectedAssignees}
          />
        </Col>
        <Col span={9}>
          <Col>
            <span>
              {intl.formatMessage({
                id: "preview.activityList.filter.types",
                defaultMessage: "Select Types",
                description: "Activity type filter on company record pane",
              })}
            </span>
            <ActivityTypeSelector
              activityTypes={activityTypes}
              onChange={handleActivityTypeChange}
              value={selectedActivityTypes}
            />
          </Col>
        </Col>
      </Row>
      <Row gutter={layout.spacerS}>
        <Col span={12}>
          <SelectField<ActivityStatusOption>
            className={styles.activitiesFilter}
            label={intl.formatMessage({
              id: "preview.activityList.statusFilter",
              defaultMessage: "Status",
              description: "Status filter for activities on preview pane",
            })}
            onChange={handleFilterChange}
            value={filter}
          >
            {[
              ActivityStatusOption.ALL,
              ActivityStatusOption.OVERDUE,
              ActivityStatusOption.UPCOMING,
              ActivityStatusOption.COMPLETED,
              ActivityStatusOption.NOT_SCHEDULED,
            ].map((filter) => (
              <Select.Option key={filter} value={filter}>
                {getActivityStatusOptionDisplayName(intl, filter)}
              </Select.Option>
            ))}
          </SelectField>
        </Col>
        <Col span={12}>
          <SelectField<RecapRange>
            className={styles.dateRangeSelect}
            disabled={filter === ActivityStatusOption.NOT_SCHEDULED}
            dropdownMatchSelectWidth={false}
            label={intl.formatMessage({
              id: "preview.activityList.timeRangeFilter",
              defaultMessage: "Time Range",
              description: "Time Range filter for activities on preview pane",
            })}
            onChange={onChangeRecapRange}
            options={recapOptions}
            value={recapRange}
          />
        </Col>
      </Row>
    </div>
  );
};

export default FilterBar;
